(() => {
  const LAUNCHER = document.querySelector('#mobile-search[data-mps="launcher"]');
  if (!LAUNCHER) return;

  // ----- constants / limits (match desktop) -----
  const LIMITS = { product: 6, query: 6, collection: 4 };
  const base = (window.routes && window.routes.predictive_search_url) || '/search/suggest.json';

  // ----- utilities -----
  const debounce = (fn, ms = 200) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const escapeHtml = (s='') => s.replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[m]));
  // Symbol-only money (e.g. "$22.38", "€21.95") — no ISO currency codes.
  const money = (cents) => {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'CAD';
    const locale = (navigator.language || 'en-CA').replace('_', '-');
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format((cents || 0) / 100);
    } catch (e) {
      return ((cents || 0) / 100).toFixed(2);
    }
  };

  function renderPriceFromPredictive(p) {
    const price =
      p.price != null ? p.price :
      p.price_min != null ? p.price_min :
      p?.variants?.[0]?.price;

    const compare =
      p.compare_at_price != null ? p.compare_at_price :
      p.compare_at_price_min != null ? p.compare_at_price_min :
      p?.variants?.[0]?.compare_at_price;

    if (price == null) return '';

    const priceHtml = moneyFromPredictive(price);
    const compareHtml = compare != null ? moneyFromPredictive(compare) : '';

    const onSale =
      compare != null &&
      Number(compare) > Number(price);

    return onSale
      ? `
        <div class="flex items-baseline gap-2">
          <span class="text-[14px] font-medium text-[var(--color-brand-strong)]">
            ${priceHtml}
          </span>
          <span class="text-[13px] font-light text-zinc-400 line-through">
            ${compareHtml}
          </span>
        </div>
      `
      : `
        <div class="text-[14px] font-light text-zinc-500">
          ${priceHtml}
        </div>
      `;
  }
  
  // Predictive API exposes price in major units (dollars); money() takes cents.
  const moneyFromPredictive = (price) => {
    const amount = Number(price || 0);
    if (Number.isNaN(amount)) return '';
    return money(Math.round(amount * 100));
  };

  const qs = (q) => new URLSearchParams({
    q,
    'resources[type]': 'product,collection,query',
    'resources[limit]': 10,          // how many Shopify returns per type
    'resources[limit_scope]': 'each' // applied separately to product/collection/query
  });

  // ----- DOM: backdrop + panel (created once) -----
  let backdrop = document.getElementById('mps-backdrop');
  if (!backdrop){ backdrop = document.createElement('div'); backdrop.id = 'mps-backdrop'; document.body.appendChild(backdrop); }

  let panel = document.getElementById('mps-panel');
  if (!panel){ panel = document.createElement('div'); panel.id = 'mps-panel'; document.body.appendChild(panel); }

  // skeleton markup (sticky header + footer, scrollable middle)
  const tpl = (q='') => `
    <div class="mps-head sticky top-0 left-0 right-0 z-[1] bg-white border-b border-black/10"
         style="padding-top:env(safe-area-inset-top);">
      <div class="px-4 py-2 flex items-center gap-2">
        <button type="button" class="mps-back h-9 w-9 rounded-full grid place-items-center">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
            </svg>
        </button>
        <div class="relative flex-1 min-w-0">
          <span class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 opacity-70">
            ${document.getElementById('search-svg-inline')?.innerHTML || '' }
          </span>
          <input id="mps-input" type="search" inputmode="search" enterkeyhint="search" autocomplete="off"
                 class="no-clear w-full h-10 rounded-full pl-9 pr-9 bg-black/5 border border-transparent
                        focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
                 placeholder="Search" value="${escapeHtml(q)}" />
          <button type="button" class="mps-clear absolute right-1.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full hidden">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"      stroke="currentColor" class="size-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <div class="mps-scroll overflow-auto" style="height:calc(100dvh - var(--mps-head) - var(--mps-foot));"></div>

    <div class="mps-foot sticky bottom-0 left-0 right-0 z-[1] bg-white border-t border-black/10"
         style="padding-bottom:calc(12px + env(safe-area-inset-bottom));">
      <div class="px-4 py-2">
        <a
            class="mps-cta flex items-baseline justify-center gap-1 border border-zinc-400/50
                    px-4 py-3 mt-3 text-[13px] font-normal tracking-wide
                    overflow-hidden"
            href="${(window.routes?.search_url || '/search')}?q=${encodeURIComponent(q)}"
            aria-label="View all results for ${escapeHtml(q)}"
            title="View all results for ${escapeHtml(q)}"
            >
            <span class="shrink-0">VIEW ALL RESULTS FOR</span>
            <!-- important: this span gets the remaining space and truncates -->
            <span class="min-w-0 truncate font-semibold" data-mps-q>${escapeHtml(q)}</span>
        </a>
      </div>
    </div>
  `;

  // section renderers
  const itemProduct = (p) => {
    const img = p.image ? `${p.image}?width=96` : '';
    const price = p.price != null ? p.price : p?.variants?.[0]?.price;

    return `
      <a href="${p.url}" class="group flex items-center gap-3 px-3 py-2">
        ${
          img
            ? `<img src="${img}" alt="${escapeHtml(p.title)}"
                    class="h-12 w-12 object-cover flex-none" loading="lazy">`
            : `<div class="h-12 w-12 bg-black/5 flex-none"></div>`
        }
        <!-- text block same height as image -->
        <div class="min-w-0 h-12 flex flex-col justify-between">
          <div class="text-[15px] text-zinc-700 font-light leading-tight line-clamp-1">
            ${escapeHtml(p.title)}
          </div>
            ${
              price != null
                ? renderPriceFromPredictive(p)
                : ``
            }
        </div>
      </a>`;
  };

  const itemQuery = (qi) => {
    const label = qi.title || qi.text || '';
    return `
      <a href="${qi.url}" class="block truncate px-4 py-2 text-[13px]">
        ${escapeHtml(label)}
      </a>`;
  };

  const itemCollection = (c) =>
    `<a href="${c.url}" class="block truncate px-4 py-2 text-[13px]">
      ${escapeHtml(c.title)}
    </a>`;

  // ----- "Did you mean" helper (mirror desktop behavior) -----
  async function handleDidYouMean(q, suggs) {
    const corrected = (suggs[0]?.title || suggs[0]?.text || '').trim();
    if (!corrected || corrected.toLowerCase() === q.toLowerCase()) return null;

    const res = await fetch(`${base}?${qs(corrected)}`, {
      headers: { Accept: 'application/json' }
    });
    if (!res.ok) return null;

    const data = await res.json();
    return {
      corrected,
      data
    };
  }

  function renderLists(data, q){
    // If did-you-mean was used, override products with those from the corrected query
    const rawProds = data.didYouMeanResults
      ? data.didYouMeanResults
      : (data?.resources?.results?.products ?? []);

    const prods = rawProds.slice(0, LIMITS.product);
    const suggs = (data?.resources?.results?.queries ?? []).slice(0, LIMITS.query);
    const colls = (data?.resources?.results?.collections ?? []).slice(0, LIMITS.collection);
    const hasQ = !!q;
    const isEmptyQuery = !q;

    // Only keep didYouMean when we actually have a query
    const didYouMean = hasQ ? (data.didYouMean || null) : null;

    // Base “no products” / “start typing” message
    const emptyBlock = isEmptyQuery
      ? `
        <div class="px-4 py-3 text-[13px] text-zinc-700">
          Begin typing to explore our catalog.
        </div>
      `
      : `
        <div class="px-4 py-3 text-[13px] text-zinc-700">
          <div>
            Sorry, nothing found for
            <span class="font-bold">'${escapeHtml(q)}'</span>.
          </div>
        </div>
      `;

    // “Did you mean …” message (shown when we used the suggestion)
    const didYouMeanBlock = didYouMean
      ? `
        <div class="px-4 py-3 text-[13px] text-zinc-700">
          <div>
            Sorry, nothing found for
            <span class="font-bold">'${escapeHtml(q)}'</span>.
          </div>
          <div class="mt-1 text-[13px] text-zinc-500">
            Did you mean
            <a href="${(window.routes?.search_url || '/search')}?q=${encodeURIComponent(didYouMean)}"
              class="ml-1 underline underline-offset-2 decoration-zinc-400 hover:decoration-zinc-600 text-zinc-700">
              ${escapeHtml(didYouMean)}
            </a>?
          </div>
        </div>
      `
      : '';

    const el = panel.querySelector('.mps-scroll');
    el.innerHTML = `
      <div class="divide-y divide-black/10">
        <div>
          <div class="relative px-4 pt-3 pb-2 mb-2 text-[11px] font-medium uppercase tracking-wide text-zinc-400">
            Product matches
            <div class="absolute bottom-0 left-4 right-4 h-px bg-zinc-200/40"></div>
          </div>
          <div class="pb-3 flex flex-col">
            ${
              didYouMean
                ? didYouMeanBlock
                : (!prods.length ? emptyBlock : '')
            }
            ${ prods.length ? prods.map(itemProduct).join('') : '' }
          </div>
        </div>

        <div>
          <div class="relative px-4 pt-3 pb-2 mb-2 text-[11px] font-medium uppercase tracking-wide text-zinc-400">
            Search suggestions
            <div class="absolute bottom-0 left-4 right-4 h-px bg-zinc-200/40"></div>
          </div>
          <div class="pb-3 flex flex-col gap-0.5">
            ${
              suggs.length
                ? suggs.map(itemQuery).join('')
                : `<div class="px-4 py-3 text-[13px] text-zinc-700">No suggestions</div>`
            }
          </div>
        </div>

        <div>
          <div class="relative px-4 pt-3 pb-2 mb-2 text-[11px] font-medium uppercase tracking-wide text-zinc-400">
            Collections
            <div class="absolute bottom-0 left-4 right-4 h-px bg-zinc-200/40"></div>
          </div>
          <div class="pb-16 flex flex-col gap-0.5">
            ${
              colls.length
                ? colls.map(itemCollection).join('')
                : `<div class="px-4 py-3 text-[13px] text-zinc-700">No collections</div>`
            }
          </div>
        </div>
      </div>
    `;

    // keep CTA in sync
    const cta = panel.querySelector('.mps-cta');
    if (cta && hasQ){
      cta.href = `${(window.routes?.search_url || '/search')}?q=${encodeURIComponent(q)}`;
      const qSlot = cta.querySelector('[data-mps-q]');
      if (qSlot) qSlot.textContent = q;
    }
  }

  // network
  let currentAbort = null;
  async function fetchAndRender(val){
    if (currentAbort) currentAbort.abort();
    currentAbort = new AbortController();
    try{
      const res = await fetch(`${base}?${qs(val)}`, {
        headers: { Accept: 'application/json' },
        signal: currentAbort.signal
      });
      if (!res.ok) return;
      const data = await res.json();

      const products = data?.resources?.results?.products ?? [];
      const suggs    = data?.resources?.results?.queries ?? [];

      // If NO products but we HAVE suggestions -> "Did you mean" flow
      if (!products.length && suggs.length) {
        const didYouMeanData = await handleDidYouMean(val, suggs);
        if (didYouMeanData) {
          renderLists({
            ...data,
            didYouMean: didYouMeanData.corrected,
            didYouMeanResults: didYouMeanData.data?.resources?.results?.products ?? []
          }, val);
          return;
        }
      }

      // Normal render
      renderLists(data, val);
    } catch {} finally { currentAbort = null; }
  }

  // open/close
  function measureFixed() {
    const head = panel.querySelector('.mps-head');
    const foot = panel.querySelector('.mps-foot');
    panel.style.setProperty('--mps-head', `${head?.offsetHeight || 0}px`);
    panel.style.setProperty('--mps-foot', `${foot?.offsetHeight || 0}px`);
  }

  function openSheet(initialValue = LAUNCHER.value.trim()){
    panel.innerHTML = tpl(initialValue);
    document.body.classList.add('mps-lock');
    panel.classList.add('mps-open');
    backdrop.classList.add('mps-open');

    // wire header controls
    const input = panel.querySelector('#mps-input');
    const btnBack = panel.querySelector('.mps-back');
    const btnClear = panel.querySelector('.mps-clear');

    // iOS keeps the keyboard up only if focus moves to another field WITHIN the
    // same user-gesture task — and never lands on nothing in between. The panel
    // was display:none, so force a style flush before focusing it.
    void panel.offsetHeight;
    input.focus({ preventScroll: true });
    try { input.setSelectionRange(input.value.length, input.value.length); } catch (e) {}

    const syncClearVis = () => btnClear.classList.toggle('hidden', !input.value);
    syncClearVis();

    btnBack.addEventListener('click', closeSheet);
    btnClear.addEventListener('click', (e) => {
      e.preventDefault();
      input.value = '';
      LAUNCHER.value = '';
      syncClearVis();
      // show empty state
      renderLists({ resources:{ results:{ products:[], queries:[], collections:[] } } }, '');
      input.focus();
    });

    input.addEventListener('input', debounce(async () => {
      LAUNCHER.value = input.value;
      syncClearVis();
      const v = input.value.trim();
      if (!v){
        renderLists({ resources:{ results:{ products:[], queries:[], collections:[] } } }, '');
        return;
      }
      await fetchAndRender(v);
    }, 180));

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.preventDefault(); closeSheet(); }
    });

    // measure & initial fetch (focus already moved synchronously above)
    requestAnimationFrame(() => {
      measureFixed();
      const v = input.value.trim();
      if (v) fetchAndRender(v);
      else renderLists({ resources:{ results:{ products:[], queries:[], collections:[] } } }, '');
    });
  }

  function closeSheet(){
    if (currentAbort) { try{ currentAbort.abort(); }catch{} }
    panel.classList.remove('mps-open'); panel.innerHTML = '';
    backdrop.classList.remove('mps-open');
    document.body.classList.remove('mps-lock');
    LAUNCHER.blur();
  }

  backdrop.addEventListener('click', closeSheet);
  window.addEventListener('resize', debounce(() => {
    if (panel.classList.contains('mps-open')) measureFixed();
  }, 120), { passive:true });
  window.addEventListener('orientationchange', () => {
    if (panel.classList.contains('mps-open')) setTimeout(measureFixed, 200);
  }, { passive:true });

  // launch only after user has typed at least 1 character in the mobile header field
  const MIN_CHARS = 1;

  LAUNCHER.addEventListener('input', () => {
    if (window.innerWidth >= 768) return; // desktop/tablet: do nothing

    const v = LAUNCHER.value.trim();
    const alreadyOpen = panel.classList.contains('mps-open');

    if (v.length >= MIN_CHARS && !alreadyOpen) {
      // openSheet() moves focus to the sheet input synchronously. Do NOT blur
      // the launcher — on iOS that closes the keyboard and the user loses
      // their typing flow until they tap the field again.
      openSheet(v);
    }
  });
})();
